import { Reveal } from "@components/ui";

const steps = [
  {
    number: 1,
    title: "Application Review",
    description: "CV screening by HR & department heads.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
        <polyline points="14 2 14 8 20 8" />
        <line x1="16" y1="13" x2="8" y2="13" />
        <line x1="16" y1="17" x2="8" y2="17" />
      </svg>
    ),
  },
  {
    number: 2,
    title: "Shortlisting",
    description: "Candidates meeting role requirements are shortlisted.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
        <circle cx="8.5" cy="7" r="4" />
        <polyline points="17 11 19 13 23 9" />
      </svg>
    ),
  },
  {
    number: 3,
    title: "Interviews",
    description: "HR interview, technical or departmental interview, and practical assessment for technical roles.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
      </svg>
    ),
  },
  {
    number: 4,
    title: "Offer Letter",
    description: "Offer issued to selected candidates with full compensation details.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="2" y="4" width="20" height="16" rx="2" />
        <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7" />
      </svg>
    ),
  },
  {
    number: 5,
    title: "Onboarding",
    description: "Document verification, system setup, and orientation program.",
    icon: (
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <polyline points="22 4 12 14.01 9 11.01" />
      </svg>
    ),
  },
];

const RecruitmentProcessSection = () => {
  return (
    <section className="py-12 md:py-16 lg:py-[120px] px-5 md:px-10 lg:px-[180px]">
      <div className="w-full max-w-[1368px] mx-auto">
        <Reveal>
          <div className="flex flex-col items-center text-center mb-10 md:mb-14">
            <span className="inline-block px-4 py-1.5 rounded-full bg-[#122d63]/10 text-[#122d63] text-[14px] md:text-[16px] font-medium tracking-[-0.04em] mb-4">
              How It Works
            </span>
            <h2 className="text-[28px] md:text-[44px] lg:text-[60px] tracking-[-0.04em] font-semibold text-[#122d63]">
              Recruitment Process
            </h2>
            <p className="text-[20px] md:text-[24px] lg:text-[28px] text-[#62636c] tracking-[-0.04em] mt-3 max-w-[700px]">
              Your journey to joining GAZ
            </p>
          </div>
        </Reveal>

        {/* Timeline */}
        <div className="relative">
          {/* Vertical connecting line — desktop only */}
          <div className="hidden lg:block absolute left-[39px] top-[40px] bottom-[40px] w-[2px] bg-gradient-to-b from-[#f03813] via-[#f03813]/40 to-[#122d63]" />

          <div className="flex flex-col gap-5 lg:gap-0">
            {steps.map((step, index) => (
              <Reveal key={step.number}>
                <div className="group relative flex items-start gap-5 lg:gap-8 lg:py-6">
                  {/* Step number badge */}
                  <div className="relative z-10 shrink-0 w-[50px] h-[50px] lg:w-[78px] lg:h-[78px] rounded-full bg-white border-[3px] border-[#f03813] flex items-center justify-center group-hover:bg-[#f03813] transition-all duration-300">
                    <span className="text-[18px] lg:text-[28px] font-bold text-[#f03813] group-hover:text-white transition-colors duration-300">
                      {String(step.number).padStart(2, "0")}
                    </span>
                  </div>

                  {/* Content card */}
                  <div className="flex-1 bg-white rounded-[20px] border border-[rgba(98,99,108,0.15)] p-5 md:p-7 flex items-start gap-5 group-hover:shadow-[0_8px_30px_rgba(0,0,0,0.08)] group-hover:border-[rgba(240,56,19,0.2)] transition-all duration-300">
                    {/* Icon */}
                    <div className="hidden sm:flex shrink-0 w-12 h-12 rounded-[14px] bg-[#f03813]/8 items-center justify-center text-[#f03813] group-hover:bg-[#f03813] group-hover:text-white transition-all duration-300">
                      {step.icon}
                    </div>

                    <div className="flex-1">
                      <h3 className="text-[18px] md:text-[22px] lg:text-[24px] font-semibold text-[#090d2c] tracking-[-0.04em]">
                        {step.title}
                      </h3>
                      <p className="text-[15px] md:text-[18px] lg:text-[20px] text-[#62636c] tracking-[-0.04em] mt-1.5 leading-relaxed">
                        {step.description}
                      </p>
                    </div>

                    {/* Step indicator — faint on right */}
                    <span className="hidden md:block text-[48px] lg:text-[64px] font-bold text-[#f5f5f7] leading-none select-none group-hover:text-[#fef0ed] transition-colors duration-300">
                      {String(step.number).padStart(2, "0")}
                    </span>
                  </div>
                </div>

                {/* Mobile connector line between steps */}
                {index < steps.length - 1 && (
                  <div className="lg:hidden flex justify-start pl-[23px]">
                    <div className="w-[3px] h-6 bg-gradient-to-b from-[#f03813]/30 to-[#f03813]/10 rounded-full" />
                  </div>
                )}
              </Reveal>
            ))}
          </div>
        </div>

        <Reveal>
          <div className="mt-10 md:mt-14 rounded-[20px] bg-gradient-to-r from-[#122d63] to-[#1a3f7a] py-6 md:py-8 px-6 md:px-10 lg:px-12 flex items-start gap-4">
            <div className="hidden sm:flex shrink-0 w-10 h-10 rounded-full bg-white/10 items-center justify-center mt-0.5">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M12 16v-4M12 8h.01" />
              </svg>
            </div>
            <p className="text-[15px] md:text-[18px] lg:text-[22px] text-white/90 tracking-[-0.04em] leading-relaxed">
              GAZ is an equal opportunity employer. We do not discriminate
              based on race, religion, nationality, gender, age, or disability.
              Employment decisions are made solely on merit, competence, and
              organizational needs.
            </p>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

export default RecruitmentProcessSection;
